import React from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import {withRouter} from 'react-router-dom';
import BigNumber from 'bignumber.js';
import LayoutContainer from '../../components/LayoutContainer';
import Title from '../../components/Title';
import Box from '../../components/Box';
import BottomImage from '../../components/BottomImage';
import {Field, Form, FormValidation, untouch} from '@nexios/frontend-forms';
import Button from '../../components/Button';
import MainContainer from '../../components/MainContainer';
import Checkbox from '../../components/Checkbox';
import Logo from '../../components/Logo';
import DisplayIban from '../../components/DisplayIban';
import ProgressBar from '../../components/ProgressBar';
import parseAndGetBigNumber from '../../utils/parseAndGetBigNumber';
import ButtonContainer from '../../components/ButtonContainer';
import InputAmount from '../../components/InputAmount';
import FieldWrapper from '../../components/FieldWrapper';
import ErrorMessage from '../../components/ErrorMessage';
import {updateSession} from '../../reducer';
import {gaStep1} from '../../googleAnalytics/step1';
import Dropdown from '../../components/Dropdown';
import {Helmet} from 'react-helmet';
import TextArea from '../../components/TextArea';
import TextToHtml from '../../components/TextToHtml';
import RadioButtonContainer from '../../components/RadioButtonContainer';
import useScrollToTop from '../../hooks/useScrollToTop';
import Teaser from '../../components/Teaser';
import CollectingSideBar from '../../components/CollectingSideBar';
import DonationBoxSideBar from '../../components/DonationBoxSideBar';
import SinglePeriod from '../../components/SingePeriod';
import parseText from '../../utils/parseText';
import SmsOptOut from '../../components/SmsOptOut';
import SelectedAmountText from '../../components/SelectedAmountText';
import SelectedFrequencyContent from '../../components/SelectedFrequencyContent';
import {withTheme} from 'styled-components';
import {wrap as wrapTitle} from '../../components/TitleWrapper';
import DJSBSideBar from '../../components/DJSBSideBar';
import CollectingImage from '../../components/CollectingImage';
import {Button as Backbutton} from '../../components/Backbutton';

BigNumber.config({
  FORMAT: {
    decimalSeparator: ',',
    groupSeparator: '.',
    groupSize: 3
  }
});

const getDefaultPeriod = formContext => {
  const periodMapping = {
    threeMonths: '3',
    sixMonths: '6',
    nineMonths: '9',
    twelveMonths: '12'
  };
  return periodMapping[formContext.defaultPeriod];
};

function formatAmount(amount, options = {}) {
  const {
    showCurrencySymbol = true,
    showEuroText = false,
    showDashForWholeNumber = false
  } = options;

  const isWholeNumber = Number.isInteger(Number(amount));
  let formatted = new Intl.NumberFormat('nl-NL', {
    style: showCurrencySymbol ? 'currency' : 'decimal',
    currency: 'EUR',
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    useGrouping: true
  }).format(amount);

  if (isWholeNumber && showDashForWholeNumber) {
    formatted += ',-';
  }

  if (showEuroText) {
    formatted += ' euro';
  }

  return formatted;
}

const onFrequencyChange = (formContext, theme, frequency, {setList, setValues}) => {
  let theSelectedAmount = frequency.amounts.find(a => a.amount_m === frequency.defaultAmount_m);
  const showIcon = formContext.amountDisplay === 'icon';
  const amounts = frequency.amounts.map(a => {
    const name = formatAmount(a.amount_m, {
      showCurrencySymbol: formContext.amountDisplay === 'amount',
      showEuroText: formContext.amountDisplay === 'amountEuroText',
      showDashForWholeNumber: !!theme.showDashForWholeNumberInAmountButtons
    });
    return {
      label: showIcon ? a.label : name,
      value: {
        value: a.amount_m.toString(),
        icon: showIcon ? a.icon : null,
        ribbon: a.amount_m === frequency.defaultAmount_m ? frequency.defaultRibbon : ''
      }
    };
  });

  if (formContext.customAmountDisplay === 'button' && frequency.allowCustomAmount) {
    amounts.push({
      label: 'Anders',
      value: {
        value: true,
        fieldName: 'showCustomAmount',
        icon: null,
        ribbon: ''
      }
    });
  }

  setList('amounts', amounts);

  if (!theSelectedAmount && frequency.amounts.length === 1) {
    theSelectedAmount = frequency.amounts[0];
  }

  const values = {
    showCustomAmount: formContext.customAmountDisplay === 'inline' && frequency.allowCustomAmount,
    allowCustomAmount: frequency.allowCustomAmount,
    amountLabel: theSelectedAmount ? theSelectedAmount.label : null,
    donationCustomAmount_m: null,
    donationAmount_m: frequency.defaultAmount_m || null
  };

  if (formContext.mandateType === 'temporary' && frequency.value === 'monthly') {
    values.period = getDefaultPeriod(formContext);
  } else if (formContext.mandateType !== 'temporary' || frequency.value !== 'monthly') {
    values.period = null;
  }

  setValues(values);
};

const Step1 = ({formContext, dmBoxInfo, collectorInfo, save, history, gaStep1, untouchAmountFields, next, progressBar, theme}) => {
  useScrollToTop();
  const formConfig = {
    onSubmit: () => {
      gaStep1(formContext);
      save([
        'frequency', 'showCustomAmount', 'customAmount_m', 'amount_m', 'variableFieldAnswer', 'period', 'amountLabel', 'optInAdminCostContribution', 'adminCostContributionAmount_m', 'donationCustomAmount_m',
        'donationAmount_m'
      ], () => {
        history.push(`${next}${history.location.search}`);
      });
    },
    onDataChanged: (ctx) => {
      const hasAmountChanged = _.get(ctx, ['data', 'donationAmount_m']) !== _.get(ctx, ['previousData', 'donationAmount_m']);

      if (hasAmountChanged && _.get(ctx, ['data', 'donationAmount_m']) !== null) {
        ctx.setValues({
          donationCustomAmount_m: null
        });

        if (formContext.customAmountDisplay === 'button') {
          ctx.setValues({
            showCustomAmount: false
          });
        }
      }

      const showCustomAmountNewValue = _.get(ctx, ['data', 'showCustomAmount']);
      const showCustomAmountPreviousValue = _.get(ctx, ['previousData', 'showCustomAmount']);
      const showCustomAmountChanged = showCustomAmountNewValue !== showCustomAmountPreviousValue;

      if (showCustomAmountChanged && showCustomAmountNewValue === false) {
        ctx.setValues({
          donationCustomAmount_m: null
        });
      }

      if (showCustomAmountChanged && showCustomAmountNewValue === true && formContext.customAmountDisplay === 'button') {
        // Alleen bij een klik op de knop is ctx.field gevuld; bij een submit is hij leeg.
        // Zonder deze check zouden we tijdens een submit net berekende fouten wissen en
        // zou het formulier zonder bedrag verstuurd kunnen worden.
        if (ctx.field === 'showCustomAmount') {
          untouchAmountFields();
        }

        ctx.setValues({
          donationAmount_m: null
        });
      }

      const frequency = formContext.frequencies.find(f => f.value === _.get(ctx, ['data', 'frequency']));

      if (frequency) {
        if (ctx.data.frequency !== ctx.previousData.frequency) {
          onFrequencyChange(formContext, theme, frequency, {
            setList: ctx.setList,
            setValues: ctx.setValues
          });
        }

        const theSelectedAmount = frequency.amounts.find(a => a.amount_m === ctx.data.donationAmount_m);

        if (theSelectedAmount) {
          ctx.setValues({
            amountLabel: theSelectedAmount ? theSelectedAmount.label : null
          });
        } else {
          ctx.setValues({
            amountLabel: null
          });
        }
      }

      //Update the final values for amount and customAmount
      if (ctx.data.optInAdminCostContribution === true && formContext.askAdminCostContribution === true && ctx.data.frequency === 'oneOff') {
        ctx.setValues({
          amount_m: !_.isNil(ctx.data.donationAmount_m) ? `${((+ctx.data.donationAmount_m) + (+ctx.data.adminCostContributionAmount_m)).toFixed(2)}` : null,
          customAmount_m: !_.isNil(ctx.data.donationCustomAmount_m) ? `${((+ctx.data.donationCustomAmount_m) + (+ctx.data.adminCostContributionAmount_m)).toFixed(2)}` : null
        });
      } else {
        ctx.setValues({
          amount_m: ctx.data.donationAmount_m,
          customAmount_m: ctx.data.donationCustomAmount_m
        });
      }

      ctx.validate(true);
    },
    onInit: (ctx) => {
      let frequency;

      ctx.setDefaultValues({
        optInAdminCostContribution: formContext.askAdminCostContribution === true ? _.get(formContext, 'adminCostContributionDefault') : false,
        adminCostContributionAmount_m: formContext.adminCostContributionAmount_m
      });

      const customAmount = _.get(ctx, ['donationCustomAmount_m']);
      if (customAmount) {
        ctx.setValues({
          donationAmount_m: customAmount
        });
      }

      if (_.isNil(ctx.frequency)) {
        frequency = formContext.frequencies.find(f => f.value === formContext.defaultFrequency);
      } else {
        // use frequency stored in session
        frequency = formContext.frequencies.find(f => f.value === ctx.frequency);
      }

      if (!_.isNil(frequency)) {
        ctx.setDefaultValues({
          frequency: frequency.value
        });

        onFrequencyChange(formContext, theme, frequency, {
          setList: ctx.setList,
          setValues: ctx.setDefaultValues
        });
      }

      ctx.setList('frequencies', formContext.frequencies);
      if (formContext.mandateType === 'temporary') {
        const periods = [];
        if (formContext.allowPeriodThreeMonths === true) {
          periods.push({label: 'Stopt automatisch na 3 maanden', value: '3'});
        }
        if (formContext.allowPeriodSixMonths === true) {
          periods.push({label: 'Stopt automatisch na 6 maanden', value: '6'});
        }
        if (formContext.allowPeriodNineMonths === true) {
          periods.push({label: 'Stopt automatisch na 9 maanden', value: '9'});
        }
        if (formContext.allowPeriodTwelveMonths === true) {
          periods.push({label: 'Stopt automatisch na 12 maanden', value: '12'});
        }
        ctx.setList('periods', periods);
      }

      if (_.isNil(ctx.period) && !_.isNil(formContext.defaultPeriod)) {
        ctx.setDefaultValues({
          period: getDefaultPeriod(formContext)
        });
      }

      const customAmountDisplay = _.get(formContext, ['customAmountDisplay']);
      let showInline = customAmountDisplay === 'inline' && frequency.allowCustomAmount;
      let showBecauseShowCustomAmount = ctx.showCustomAmount;

      if (showInline || showBecauseShowCustomAmount) {
        ctx.setValues({
          showCustomAmount: true
        });
      } else {
        ctx.setValues({
          showCustomAmount: false
        });
      }

      // Update the final values for amount and customAmount
      if (
        ctx.frequency === 'oneOff' && formContext.askAdminCostContribution === true
          ? _.get(formContext, 'adminCostContributionDefault')
          : false
      ) {
        ctx.setValues({
          amount_m: !_.isNil(ctx.donationAmount_m) ? `${(+ctx.donationAmount_m + +ctx.adminCostContributionAmount_m).toFixed(2)}` : null,
          customAmount_m: !_.isNil(ctx.donationCustomAmount_m)
            ? `${(+ctx.donationCustomAmount_m + +ctx.adminCostContributionAmount_m).toFixed(2)}`
            : null
        });
      } else {
        ctx.setValues({
          amount_m: ctx.donationAmount_m,
          customAmount_m: ctx.donationCustomAmount_m
        });
      }
    },
    fields: {
      allowCustomAmount: {
        isVisible: ctx => ctx.allowCustomAmount === true && formContext.customAmountDisplay === 'toggle'
      },
      donationCustomAmount_m: {
        isVisible: (ctx) => ctx.showCustomAmount === true,
        rules: [
          {
            isValid: ctx => {
              if (!ctx.showCustomAmount) {
                return true;
              }

              const amount = _.get(ctx, ['donationAmount_m']);
              const amountHasValue = amount !== '' && !_.isNil(amount);

              if (amountHasValue && formContext.customAmountDisplay === 'inline') {
                return true;
              }

              const frequency = formContext.frequencies.find(f => f.value === _.get(ctx, ['frequency']));

              const maxAmount = BigNumber(frequency.maxAmount_m || '100000');
              const minAmount = BigNumber(frequency.minAmount_m || '1');

              const amountBn = parseAndGetBigNumber(ctx.customAmount_m);

              if (_.isNil(amountBn) || amountBn.isNaN()) {
                return true;
              }

              return amountBn.isGreaterThanOrEqualTo(minAmount) && amountBn.isLessThanOrEqualTo(maxAmount);
            },
            message: ctx => {
              const frequency = formContext.frequencies.find(f => f.value === _.get(ctx, ['frequency']));

              const maxAmount = BigNumber(frequency.maxAmount_m || '100000');
              const minAmount = BigNumber(frequency.minAmount_m || '1');

              return `Vul een bedrag in tussen € ${minAmount.toFormat(2)} en € ${maxAmount.toFormat(2)}`;
            }
          },
          {
            isValid: ctx => {
              if (!ctx.showCustomAmount) {
                return true;
              }

              const amount = _.get(ctx, ['donationAmount_m']);
              const amountHasValue = amount !== '' && !_.isNil(amount);

              if (amountHasValue && formContext.customAmountDisplay === 'inline') {
                return true;
              }

              const amountBn = parseAndGetBigNumber(ctx.customAmount_m);
              return !_.isNil(amountBn) && !amountBn.isNaN();
            },
            message: theme.showRequiredText && 'Bedrag is niet ingevuld'
          }
        ]
      },
      frequency: {
        isVisible: () => true,
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.frequency),
            message: theme.showRequiredText && 'Frequentie is niet ingevuld'
          }
        ]
      },
      period: {
        isVisible: (ctx) => formContext.mandateType === 'temporary' && ctx.frequency === 'monthly',
        rules: [
          {
            isValid: ctx => {
              if (formContext.mandateType === 'temporary') {
                return _.includes(['3', '6', '9', '12'], ctx.period);
              }
              return true;
            },
            message: theme.showRequiredText && 'Periode is niet ingevuld'
          }
        ]
      },
      donationAmount_m: {
        isVisible: (ctx) => {
          return formContext.customAmountDisplay === 'toggle' ? ctx.showCustomAmount !== true : true;
        },
        rules: [
          {
            isValid: ctx => {
              const amountHasValue = !_.isNil(ctx.donationAmount_m) && ctx.donationAmount_m !== '';
              const customAmountHasValue = !_.isNil(ctx.donationCustomAmount_m) && ctx.donationCustomAmount_m !== '';

              return amountHasValue || customAmountHasValue;
            },
            message: theme.showRequiredText && 'Bedrag is niet ingevuld'
          }
        ]
      },
      variableFieldAnswer: {
        isVisible: ctx => ctx.isVariableFieldVisible && (ctx.donationPageType === '10_donation' || ctx.donationPageType === '12_donationAnon'),
        isRequired: ctx => ctx.isVariableFieldRequired,
        rules: [
          {
            isValid: ctx => {
              if (ctx.isVariableFieldRequired) {
                return !_.isNil(ctx.variableFieldAnswer) && _.trim(ctx.variableFieldAnswer) !== '';
              }

              return true;
            },
            message: theme.showRequiredText && 'Dit veld is vereist'
          }
        ]
      },
      optInAdminCostContribution: {
        isVisible: ctx => ctx.frequency === 'oneOff' && formContext.askAdminCostContribution === true
      }
    }
  };

  return <>
    <Helmet>
      <title>{(formContext.isDonationBox && dmBoxInfo?.boxHeader_txt) || formContext.amountBrowserTitle}</title>
    </Helmet>
    <MainContainer
      backgroundImageUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageUrl}
      backgroundImageTabletUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageTabletUrl}
      backgroundImageMobileUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageMobileUrl}
    />
    <LayoutContainer
      showCollectingWidget={formContext.showCollectingWidget || formContext.isDonationBox}
      position={formContext.textPosition}
      header={<div>
        <div style={{float: 'left', display: 'inline-block'}}>
          <Logo url={formContext.websiteUrl} />
        </div>
        {formContext.isCollecting && theme?.collecting?.imageTopRight &&
          <div style={{float: 'right', display: 'inline-block'}}><CollectingImage collectorInfo={collectorInfo} /></div>}
        <DisplayIban iban='NL08 INGB 0000 0005 55' />
      </div>}
      content={<>
        <Box position={formContext.textPosition}
             hasTeaser={!_.isNil(formContext.pageTeaserHeader) || !_.isNil(formContext.pageTeaserBodyText) || !_.isEmpty(formContext.pageTeaserCarouselItems)}>
          <Title title={formContext.step1Header}><ProgressBar progress={progressBar} /></Title>
          <Form
            formInstanceKey='donate'
            formContext={formContext}
            formConfig={formConfig}>
            {!theme?.error?.showBelowBodyText && <FormValidation
              component={ErrorMessage}
              errorMessageTemplate={formContext.errorMessageTemplate}
              formContext={formContext}
              errorMessage='Je hebt nog niet alle velden ingevuld.' />}
            <div className='widgetContent'>
              {theme?.title?.showInBody && <TextToHtml text={formContext.step1Header} wrap={wrapTitle} />}
              <TextToHtml text={formContext.step1BodyText} />
              {theme?.error?.showBelowBodyText && <FormValidation
                component={ErrorMessage}
                errorMessageTemplate={formContext.errorMessageTemplate}
                formContext={formContext}
                errorMessage='Je hebt nog niet alle velden ingevuld.' />}
              <Field
                list='frequencies'
                name='frequency'
                component={(props) => {
                  const list = _.get(props, ['list']);
                  // do not render options if there is only 1 frequency
                  if (_.size(list) < 2 && formContext.frequencyDisplay === 'dropdown') {
                    return null;
                  }

                  const enrichedFrequencies = list.map(item => ({...item, ribbon: _.find(formContext.frequencies, f => f.value === item.value)?.ribbon || ''}));

                  return (
                    <div className='marginBottom'>
                      {formContext.frequencyDisplay === 'dropdown' && <Dropdown {...props} />}
                      {formContext.frequencyDisplay === 'radiobutton' && <RadioButtonContainer {...props} />}
                      {formContext.frequencyDisplay === 'button' && <ButtonContainer fieldName='frequency' list={enrichedFrequencies} />}
                    </div>
                  );
                }}
              />

              <Field
                name='frequency'
                component={() => <SelectedFrequencyContent formContext={formContext} />} />

              <Field
                list='amounts'
                name='donationAmount_m'
                component={(props) => {
                  const list = props.list.map(a => {
                    a.ribbon = a.value.ribbon;
                    a.icon = a.value.icon;
                    a.fieldName = a.value.fieldName;
                    a.value = a.value.value;
                    return a;
                  });

                  return <div className='marginBottom amountButtons'>
                    <ButtonContainer
                      fieldName='donationAmount_m'
                      valueName='donationAmount_m'
                      list={list} /></div>;
                }}
              />

              {formContext.customAmountDisplay !== 'inline' && <Field
                name='allowCustomAmount'
                component={() => <div className='marginBottom centered'>
                  <Field
                    name='showCustomAmount'
                    component={({setValue, value}) => value ? null :
                      <button type='button' className='link' onClick={() => {
                        setValue(true);
                      }}>
                        Of kies een ander bedrag
                      </button>}
                  />
                </div>}
              />}

              <div className='marginBottom'>
                {formContext.customAmountDisplay !== 'inline' && <Field
                  name='donationCustomAmount_m'
                  component={() => <div>Vul het bedrag in dat je wilt doneren</div>}
                />}
                <Field
                  name='donationCustomAmount_m'
                  placeholder={formContext.customAmountDisplay === 'inline' ? 'Of vul hier een ander bedrag in' : '0,00'}
                  component={FieldWrapper}
                  onFocus={(e, updateField) => updateField('donationAmount_m', null)}
                  inputComponent={InputAmount}
                />
              </div>

              <div className='marginBottom'>
                <Field
                  name='donationAmount_m'
                  component={() => <SelectedAmountText formContext={formContext} amountField='donationAmount_m' className='center' />}
                />
              </div>

              <Field
                list='periods'
                component={(props) => {
                  const list = _.get(props, ['list']);
                  // do not render options if there is only 1 frequency
                  if (_.size(list) === 0) {
                    return null;
                  }
                  if (_.size(list) === 1) {
                    return <SinglePeriod label={list[0].label} />;
                  }
                  return <div className='marginBottom'>
                    {formContext.periodDisplay === 'dropdown' && <Dropdown {...props} placeholder='Selecteer een periode...' />}
                    {formContext.periodDisplay === 'radiobutton' && <RadioButtonContainer {...props} />}
                    {formContext.periodDisplay === 'button' && <ButtonContainer fieldName='period' list={list} />}
                  </div>;
                }}
                name='period'
              />

              <div className='marginBottom'>
                <Field
                  label={_.get(formContext, 'variableFieldLabel')}
                  name={'variableFieldAnswer'}
                  inputComponent={TextArea}
                  component={FieldWrapper}
                  placeholder={_.get(formContext, 'variableFieldPlaceholder')}
                />
              </div>

              <div className='marginBottom'>
                <Field
                  label={parseText(formContext, formContext.adminCostContributionText)}
                  name='optInAdminCostContribution'
                  component={FieldWrapper}
                  inputComponent={Checkbox}
                  hideLabel
                />
              </div>

              <div className='marginBottom'>
                <FormValidation
                  type='submit'
                  label={formContext.amountCallToActionLabel || 'Doneer'}
                  component={Button}
                />
              </div>

              {formContext.entryCallToAction &&
                <div className='marginBottom centered'>
                  {formContext.entryCallToActionUrl ? <a className='link' href={formContext.entryCallToActionUrl} rel='noopener noreferrer'>{formContext.entryCallToAction}</a> : formContext.entryCallToAction}
                </div>}
              {formContext.customAmountDisplay === 'toggle' && <Field
                name='showCustomAmount'
                component={({setValue, value}) => value ?
                  <Backbutton type='button' className='link' onClick={() => setValue(false)}>Terug</Backbutton> : null} />}
              <BottomImage url={formContext.bottomImageUrl} altText={formContext.bottomImageAltText} />
            </div>
          </Form>
        </Box>
        <SmsOptOut text={formContext.smsOptOutText} processedText={formContext.smsOptOutProcessedText} />
      </>}
      sidebar={<>
        {theme?.djsb?.showDJSB && <DJSBSideBar formContext={formContext} />}
        {formContext.showCollectingWidget
          ? <CollectingSideBar collectorInfo={collectorInfo}><Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} /></CollectingSideBar>
          : formContext.isDonationBox
            ? <DonationBoxSideBar dmBoxInfo={dmBoxInfo} header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText}>
              <Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} />
            </DonationBoxSideBar>
            : <Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} />}
      </>}
      faq={formContext.frequentlyAskedQuestions}
      footer={formContext.footer}
      formContext={formContext}
      step={'1'}
    />
  </>
    ;
};

const mapStateToProps = (state) => {
  return {
    formContext: state.reducer.formContext
  };
};

const mapDispatchToProps = (dispatch) => ({
  save: (fields, callback) => {
    dispatch(updateSession(fields, {
      isStep1Completed: true
    }, callback));
  },
  gaStep1: (formContext) => {
    dispatch(gaStep1(formContext));
  },
  untouchAmountFields: () => {
    dispatch(untouch({instanceKey: 'donate', field: 'donationCustomAmount_m'}));
    dispatch(untouch({instanceKey: 'donate', field: 'donationAmount_m'}));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(Step1)));
